import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAdminAlerts,
  useAcknowledgeAlert,
  useResolveAlert,
  useBulkAcknowledgeAlerts,
  useBulkResolveAlerts,
} from '@/api/hooks/useAdminAlerts';
import { useDashboardSummary } from '@/api/hooks/useAdmin';
import { formatDateTime } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ChevronLeft, ChevronRight, MapPin, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { AlertPriority, AlertStatus } from '@/api/types';

const PRIORITY_COLORS: Record<AlertPriority, string> = {
  P1: 'bg-red-500 text-white',
  P2: 'bg-orange-500 text-white',
  P3: 'bg-yellow-500 text-black',
  P4: 'bg-gray-400 text-white',
};

const STATUS_COLORS: Record<AlertStatus, string> = {
  ACTIVE: 'bg-red-100 text-red-800',
  ACKNOWLEDGED: 'bg-yellow-100 text-yellow-800',
  RESOLVED: 'bg-green-100 text-green-800',
};

const PRIORITIES: AlertPriority[] = ['P1', 'P2', 'P3', 'P4'];
const STATUSES: AlertStatus[] = ['ACTIVE', 'ACKNOWLEDGED', 'RESOLVED'];

export default function AlertsPage() {
  const { t, i18n } = useTranslation();
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [page, setPage] = useState(0);

  const { data: dashboardSummary } = useDashboardSummary();
  const locations = dashboardSummary?.locationBreakdown ?? [];

  const { data, isLoading } = useAdminAlerts({
    priority: priorityFilter !== 'all' ? priorityFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    locationId: locationFilter !== 'all' ? locationFilter : undefined,
    page,
    size: 20,
  });

  const alerts = data?.alerts ?? [];
  const acknowledgeMutation = useAcknowledgeAlert();
  const resolveMutation = useResolveAlert();
  const bulkAckMutation = useBulkAcknowledgeAlerts();
  const bulkResolveMutation = useBulkResolveAlerts();

  // Bulk selection (ids on the current page). Reset when the page/filters change.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const bulkBusy = bulkAckMutation.isPending || bulkResolveMutation.isPending;

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const allOnPageSelected = alerts.length > 0 && alerts.every((a) => selected.has(a.id));
  const toggleAllOnPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) alerts.forEach((a) => next.delete(a.id));
      else alerts.forEach((a) => next.add(a.id));
      return next;
    });

  const runBulkAck = async (req: { alertIds?: string[]; all?: boolean }) => {
    try {
      const r = await bulkAckMutation.mutateAsync(req);
      toast.success(t('admin.alerts_bulk_ack_done', { count: r.acknowledged }));
      setSelected(new Set());
    } catch {
      toast.error(t('common.error'));
    }
  };
  const runBulkResolve = async (req: { alertIds?: string[]; all?: boolean }) => {
    try {
      const r = await bulkResolveMutation.mutateAsync(req);
      toast.success(t('admin.alerts_bulk_resolve_done', { count: r.resolved }));
      setSelected(new Set());
    } catch {
      toast.error(t('common.error'));
    }
  };

  // Clear the selection when the page or filters change (ids no longer on screen).
  useEffect(() => {
    setSelected(new Set());
  }, [page, priorityFilter, statusFilter, locationFilter]);

  const handleAcknowledge = async (alertId: string) => {
    try {
      await acknowledgeMutation.mutateAsync(alertId);
      toast.success(t('admin.alert_acknowledged'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleResolve = async (alertId: string) => {
    try {
      await resolveMutation.mutateAsync(alertId);
      toast.success(t('admin.alert_resolved'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const locationLabel =
    locationFilter === 'all'
      ? t('admin.all_locations')
      : locations.find((l) => l.locationId === locationFilter)?.locationName ?? t('admin.all_locations');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('admin.alerts')}</h1>
        {/* Bulk actions: selected-set when any are checked, else act-on-all. */}
        <div className="flex flex-wrap items-center gap-2">
          {selected.size > 0 ? (
            <>
              <span className="text-sm text-muted-foreground">
                {t('admin.alerts_selected', { count: selected.size })}
              </span>
              <Button size="sm" variant="outline" disabled={bulkBusy}
                onClick={() => runBulkAck({ alertIds: [...selected] })}>
                <CheckCircle className="mr-1 h-3 w-3" /> {t('admin.acknowledge')}
              </Button>
              <Button size="sm" variant="outline" disabled={bulkBusy}
                onClick={() => runBulkResolve({ alertIds: [...selected] })}>
                <XCircle className="mr-1 h-3 w-3" /> {t('admin.resolve')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                {t('admin.alerts_clear_selection')}
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" disabled={bulkBusy}
                onClick={() => runBulkAck({ all: true })}>
                {t('admin.alerts_ack_all')}
              </Button>
              <Button size="sm" variant="outline" disabled={bulkBusy}
                onClick={() => runBulkResolve({ all: true })}>
                {t('admin.alerts_resolve_all')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Select value={priorityFilter} onValueChange={(v) => { setPriorityFilter(v ?? 'all'); setPage(0); }}>
            <SelectTrigger>
              <span className="text-sm">{priorityFilter === 'all' ? t('admin.all_priorities') : priorityFilter}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.all_priorities')}</SelectItem>
              {PRIORITIES.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v ?? 'all'); setPage(0); }}>
            <SelectTrigger>
              <span className="text-sm">{statusFilter === 'all' ? t('admin.all_statuses') : t(`admin.alert_status_${statusFilter.toLowerCase()}`)}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.all_statuses')}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{t(`admin.alert_status_${s.toLowerCase()}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {locations.length > 1 && (
            <Select value={locationFilter} onValueChange={(v) => { setLocationFilter(v ?? 'all'); setPage(0); }}>
              <SelectTrigger>
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm">{locationLabel}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.all_locations')}</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.locationId} value={loc.locationId}>{loc.locationName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {/* Alerts table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : alerts.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {t('admin.no_active_alerts')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[36px]">
                      <input
                        type="checkbox"
                        aria-label={t('admin.alerts_select_all_page')}
                        className="h-4 w-4 accent-[--color-brand] align-middle"
                        checked={allOnPageSelected}
                        onChange={toggleAllOnPage}
                      />
                    </TableHead>
                    <TableHead className="min-w-[60px]">{t('admin.col_priority')}</TableHead>
                    <TableHead className="min-w-[120px]">{t('admin.col_type')}</TableHead>
                    <TableHead className="min-w-[200px]">{t('admin.col_title')}</TableHead>
                    <TableHead className="hidden md:table-cell min-w-[100px]">{t('admin.col_status')}</TableHead>
                    <TableHead className="hidden lg:table-cell min-w-[80px]">{t('admin.col_escalation')}</TableHead>
                    <TableHead className="min-w-[140px]">{t('admin.col_time')}</TableHead>
                    <TableHead className="min-w-[140px]">{t('admin.col_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert) => (
                    <TableRow
                      key={alert.id}
                      className={cn(
                        (alert.priority === 'P1' || alert.priority === 'P2') && alert.status === 'ACTIVE' && 'bg-red-50/50 font-medium',
                        selected.has(alert.id) && 'bg-primary/5',
                      )}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          aria-label={t('admin.alerts_select_one')}
                          className="h-4 w-4 accent-[--color-brand] align-middle"
                          checked={selected.has(alert.id)}
                          onChange={() => toggleOne(alert.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs min-w-[36px] text-center ${PRIORITY_COLORS[alert.priority]}`}>
                          {alert.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{alert.type}</TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{alert.title}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[250px]">{alert.description}</div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant="secondary" className={`text-xs min-w-[90px] text-center ${STATUS_COLORS[alert.status]}`}>
                          {t(`admin.alert_status_${alert.status.toLowerCase()}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-center">
                        {alert.escalationLevel}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(alert.createdAt, i18n.language)}
                      </TableCell>
                      <TableCell>
                        {alert.status === 'ACTIVE' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAcknowledge(alert.id)}
                            disabled={acknowledgeMutation.isPending}
                          >
                            <CheckCircle className="mr-1 h-3 w-3" />
                            {t('admin.acknowledge')}
                          </Button>
                        )}
                        {alert.status === 'ACKNOWLEDGED' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResolve(alert.id)}
                            disabled={resolveMutation.isPending}
                          >
                            <XCircle className="mr-1 h-3 w-3" />
                            {t('admin.resolve')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > 20 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {t('admin.page_of', { page: page + 1, total: Math.ceil(data.total / 20) })}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="mr-1 h-4 w-4" /> {t('admin.previous')}
            </Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * 20 >= data.total} onClick={() => setPage((p) => p + 1)}>
              {t('admin.next')} <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
